import type { Point, LayoutNode } from '../types';
import { shapeCenter, shapeBounds } from './geometry';

/**
 * Compute the anchor offset using 'minimizeMovement' strategy.
 * Movement is weighted by node area — larger nodes resist movement more.
 * Returns the offset that, when applied, minimizes total weighted movement.
 */
export function computeMinimalMovementAnchor(
	original: LayoutNode[],
	arranged: { id: string; x: number; y: number }[],
): Point {
	if (original.length === 0) return { x: 0, y: 0 };

	const arrangedMap = new Map(arranged.map((a) => [a.id, a]));
	let totalWeight = 0;
	let weightedDx = 0;
	let weightedDy = 0;

	for (const node of original) {
		const a = arrangedMap.get(node.id);
		if (!a) continue;

		const bounds = shapeBounds(node.shape);
		const weight = Math.max(bounds.width * bounds.height, 1);
		const origCenter = shapeCenter(node.shape);
		const newCenter = {
			x: a.x + bounds.width / 2,
			y: a.y + bounds.height / 2,
		};

		weightedDx += (origCenter.x - newCenter.x) * weight;
		weightedDy += (origCenter.y - newCenter.y) * weight;
		totalWeight += weight;
	}

	if (totalWeight === 0) return { x: 0, y: 0 };

	return {
		x: weightedDx / totalWeight,
		y: weightedDy / totalWeight,
	};
}

/**
 * Apply anchor strategy to arranged node positions.
 * Shifts all positions by the computed offset.
 */
export function applyAnchor(
	original: LayoutNode[],
	arranged: { id: string; x: number; y: number }[],
	strategy: 'first' | 'center' | 'minimizeMovement',
): { id: string; x: number; y: number }[] {
	if (arranged.length === 0) return [];

	let offset: Point;

	switch (strategy) {
		case 'first': {
			const firstOrig = original[0];
			const firstArranged = arranged[0];
			if (!firstOrig || !firstArranged) return arranged;
			const origBounds = shapeBounds(firstOrig.shape);
			offset = {
				x: origBounds.x - firstArranged.x,
				y: origBounds.y - firstArranged.y,
			};
			break;
		}
		case 'center': {
			// Keep centroid of all nodes at original centroid
			const origCenters = original.map((n) => shapeCenter(n.shape));
			const origCentroid = {
				x: origCenters.reduce((s, c) => s + c.x, 0) / origCenters.length,
				y: origCenters.reduce((s, c) => s + c.y, 0) / origCenters.length,
			};

			const arrangedCenters = arranged.map((a) => {
				const origNode = original.find((n) => n.id === a.id);
				const bounds = origNode ? shapeBounds(origNode.shape) : { width: 0, height: 0, x: 0, y: 0 };
				return {
					x: a.x + bounds.width / 2,
					y: a.y + bounds.height / 2,
				};
			});
			const arrangedCentroid = {
				x: arrangedCenters.reduce((s, c) => s + c.x, 0) / arrangedCenters.length,
				y: arrangedCenters.reduce((s, c) => s + c.y, 0) / arrangedCenters.length,
			};

			offset = {
				x: origCentroid.x - arrangedCentroid.x,
				y: origCentroid.y - arrangedCentroid.y,
			};
			break;
		}
		case 'minimizeMovement': {
			offset = computeMinimalMovementAnchor(original, arranged);
			break;
		}
	}

	return arranged.map((a) => ({
		id: a.id,
		x: a.x + offset.x,
		y: a.y + offset.y,
	}));
}
